import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * ### 他事業所連携メッセージ API 操作クラス
 */
export default class OtherOfficeReplyMessages {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET OtherOfficeReplyMessages
   */
  public async gets(userStore: LoginUser, otherOfficeFacilityId: number, datetime: number): Promise<Array<OtherOfficeReplyMessageGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      otherOfficeFacilityId: otherOfficeFacilityId,
      datetime: datetime
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeReplyMessages, formatParams);

    const response = await this.commonService.executeGet<Array<OtherOfficeReplyMessageGetModel>>(
      userStore,
      requestUrl,
      this.constructor.name,
      {},
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * ### POST OtherOfficeReplyMessage
   */
  public async post(
    userStore: LoginUser,
    otherOfficeFacilityId: number,
    datetime: number,
    requestBody: OtherOfficeReplyMessagePostModel
  ): Promise<OtherOfficeReplyMessageGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      otherOfficeFacilityId: otherOfficeFacilityId,
      datetime: datetime
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeReplyMessages, formatParams);

    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH OtherOfficeReplyMessage
   */
  public async patch(
    userStore: LoginUser,
    otherOfficeFacilityId: number,
    datetime: number,
    replyDatetime: number,
    requestBody: OtherOfficeReplyMessagePatchModel
  ): Promise<OtherOfficeReplyMessageGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      otherOfficeFacilityId: otherOfficeFacilityId,
      datetime: datetime,
      replyDatetime: replyDatetime
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeReplyMessage, formatParams);

    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * DELETE OtherOfficeReplyMessage
   */
  public async delete(userStore: LoginUser, otherOfficeFacilityId: number, datetime: number, replyDatetime: number): Promise<OtherOfficeReplyMessageGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      otherOfficeFacilityId: otherOfficeFacilityId,
      datetime: datetime,
      replyDatetime: replyDatetime
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeReplyMessage, formatParams);

    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface OtherOfficeReplyMessageGetModel {
  /** otherOfficeMessagesのレコードを特定する一意のキー（${otherOfficeMessages.id}_${otherOfficeMessages.datetime}） */
  otherOfficeMessageKey: string;
  /** 返信日時 */
  datetime: number;
  /** 送信した施設ID */
  senderFacilityId: number;
  /** 本文 */
  text: string;
  /** 添付ファイルのS3URL */
  fileUrls: Array<FileUrl>;
  /** 送信（投稿）したユーザーID。一覧取得時にはマスクが必要 */
  userId: string;

  /** 削除フラグ */
  deleteFlag: boolean;
  /** 作成日時 */
  created: number;
  /** 更新日時 */
  updated: number;
}

export interface FileUrl {
  url: string;
  name: string;
}

export interface OtherOfficeReplyMessagePostModel {
  /** 本文 */
  text: string;
  /** 添付ファイルのS3URL */
  fileUrls: Array<FileUrl>;
}

export interface OtherOfficeReplyMessagePatchModel {
  /** 更新種別 */
  updateType: UpdateType;
  /** 本文 */
  text: string | undefined;
  /** 添付ファイルのS3URL */
  fileUrls: Array<FileUrl> | undefined;
  /** 更新日時 */
  updated: number;
}

export enum UpdateType {
  EDIT = "edit",
  UNDO = "undo"
}
